import { gatedFetch } from '../utils/requestGate';

// La URL del API se fija al COMPILAR con REACT_APP_API_URL (ver scripts/rebuild_apps_hosting.js).
// Fallback duro: si el build quedó sin env (causa 404 en admin.dominio/api), usar API de producción.
const PROD_API = 'https://api.ohbellezaoficial.com';
const RAW_API = String(process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');
const API_BASE = RAW_API || (process.env.NODE_ENV === 'production' ? PROD_API : '');
/** En local, sin REACT_APP_API_URL el proxy de CRA a veces no sirve /imagenes → API directo. */
const IMAGE_BASE =
  API_BASE ||
  (process.env.NODE_ENV !== 'production' ? 'http://localhost:8081' : PROD_API);
const TOKEN_KEY = 'oh_admin_token';

let _token = null;

export function setAuthToken(token) {
  _token = token ? String(token) : null;
  try {
    if (_token) sessionStorage.setItem(TOKEN_KEY, _token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function getAuthToken() {
  if (_token) return _token;
  try {
    _token = sessionStorage.getItem(TOKEN_KEY) || null;
  } catch {
    _token = null;
  }
  return _token;
}

export function clearAuthToken() {
  _token = null;
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

function authHeaders() {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request(path, options = {}) {
  const timeoutMs = options.timeoutMs ?? 25000;
  const { timeoutMs: _t, headers, ...fetchOpts } = options;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller && timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;
  let res;
  try {
    res = await gatedFetch(`${API_BASE}/api${path}`, {
      headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(headers || {}) },
      ...fetchOpts,
      signal: controller?.signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error('La solicitud tardó demasiado. Revisa la conexión e intenta de nuevo.');
    }
    throw new Error('No se pudo conectar con el servidor. Revisa que el API esté Running en cPanel e intenta de nuevo.');
  } finally {
    if (timer) clearTimeout(timer);
  }
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const fallback =
      res.status === 413
        ? 'La foto es muy pesada (413). Usa JPG más pequeño o actualiza el API.'
        : res.status >= 500
        ? `Error del servidor (${res.status}). Intenta de nuevo.`
        : `Error en la solicitud (${res.status})`;
    const raw = typeof json === 'object' && json && (json.error || json.message);
    throw new Error(raw || fallback);
  }
  return json;
}

export function formatCOP(value) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);
}

export function imageUrl(imagen) {
  if (!imagen) return null;
  if (imagen.startsWith('http') || imagen.startsWith('data:')) return imagen;
  const path = String(imagen).replace(/^\//, '');
  const base = IMAGE_BASE || '';
  if (!base) return `/${path}`;
  return `${base.replace(/\/$/, '')}/${path}`;
}

export const api = {
  formatCOP,
  ping: () => request('/ping'),
  login: async (username, password) => {
    const res = await request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        username: String(username || '').trim(),
        password: String(password || ''),
        app: 'admin',
      }),
    });
    if (res.data?.token) setAuthToken(res.data.token);
    return res;
  },
  me: () => request('/auth/me'),
  logout: () => request('/auth/logout', { method: 'POST' }).finally(() => clearAuthToken()),
  getAdminProductos: ({ q = '', categoria = '', foto = '', stock = '', offset = 0, limit = 50 } = {}) => {
    const params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
    if (q) params.set('q', q);
    if (categoria && categoria !== 'Todas') params.set('categoria', categoria);
    if (foto) params.set('foto', foto);
    if (stock) params.set('stock', stock);
    return request(`/admin/productos?${params}`);
  },
  getCategorias: () => request('/categorias'),
  getAdminVisitas: (estadoOrOpts = 'activas') => {
    const opts = typeof estadoOrOpts === 'string' ? { estado: estadoOrOpts } : estadoOrOpts || {};
    const params = new URLSearchParams({ estado: opts.estado || 'activas' });
    if (opts.desde) params.set('desde', opts.desde);
    if (opts.hasta) params.set('hasta', opts.hasta);
    if (opts.vendedor_id) params.set('vendedor_id', String(opts.vendedor_id));
    return request(`/admin/visitas?${params}`);
  },
  cerrarVisitasHuerfanas: () =>
    request('/admin/visitas/cerrar-huerfanas', { method: 'POST' }),
  cerrarVisitaAdmin: (id) =>
    request(`/admin/visitas/${id}/cerrar`, { method: 'POST' }),
  getRecorridoVendedor: (vendedorId, fecha) => {
    const params = new URLSearchParams();
    if (fecha) params.set('fecha', fecha);
    return request(`/admin/vendedores/${vendedorId}/recorrido?${params}`);
  },
  importarInventario: (body) =>
    request('/admin/inventario/importar', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    }),
  importarClientesTxt: (body) =>
    request('/admin/clientes/importar-txt', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    }),
  importarSucursalesTxt: (body) =>
    request('/admin/clientes/importar-sucursales-txt', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    }),
  getClientesImportaciones: () => request('/admin/clientes/importaciones'),
  getInventarioCambios: ({ codigo = '', limit = 100 } = {}) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (codigo) params.set('codigo', codigo);
    return request(`/admin/inventario/cambios?${params}`);
  },
  getInventarioImportaciones: () => request('/admin/inventario/importaciones'),
  getInventarioConfig: () => request('/admin/inventario/config'),
  getAdminDashboard: () => request('/admin/dashboard'),
  getAdminPedidos: (
    { estado, q, desde, hasta, limit = 100, resumen = false, por, vendedor_id, preparado_por_id } = {}
  ) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (estado) params.set('estado', estado);
    if (q) params.set('q', q);
    if (desde) params.set('desde', desde);
    if (hasta) params.set('hasta', hasta);
    if (resumen) params.set('resumen', '1');
    if (por) params.set('por', por);
    if (vendedor_id) params.set('vendedor_id', String(vendedor_id));
    if (preparado_por_id) params.set('preparado_por_id', String(preparado_por_id));
    return request(`/admin/pedidos?${params}`);
  },
  getAdminPedido: (id) => request(`/admin/pedidos/${id}`),
  /** Crear producto nuevo (código, precios, categoría, stock, canales, foto opcional). */
  createProducto: (body) =>
    request('/admin/productos', {
      method: 'POST',
      body: JSON.stringify(body),
      timeoutMs: 90000,
    }),
  updateProducto: (id, body) =>
    request(`/productos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  /** Misma familia de URL que updateProducto (PUT /productos/:id). */
  deleteProducto: async (id) => {
    try {
      return await request(`/productos/${id}`, { method: 'DELETE' });
    } catch (err) {
      const msg = String(err?.message || '');
      if (!/404|Cannot DELETE|no encontrad|Not Found/i.test(msg)) throw err;
      return request(`/admin/productos/${id}`, { method: 'DELETE' });
    }
  },
  /** Sube foto PNG/JPG (data URL base64) al producto. */
  uploadProductoImagen: (id, { data_url, imagen_base64, content_type, original_name } = {}) =>
    request(`/admin/productos/${id}/imagen`, {
      method: 'POST',
      body: JSON.stringify({ data_url, imagen_base64, content_type, original_name }),
      timeoutMs: 90000,
    }),
  getProductoOpciones: (id) => request(`/admin/productos/${id}/opciones`),
  addProductoOpcion: (id, body = {}) =>
    request(`/admin/productos/${id}/opciones`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  patchProductoOpcion: (id, oid, body = {}) =>
    request(`/admin/productos/${id}/opciones/${oid}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  uploadProductoOpcionImagen: (id, oid, { data_url, original_name } = {}) =>
    request(`/admin/productos/${id}/opciones/${oid}/imagen`, {
      method: 'POST',
      body: JSON.stringify({ data_url, original_name }),
      timeoutMs: 90000,
    }),
  deleteProductoOpcion: (id, oid) =>
    request(`/admin/productos/${id}/opciones/${oid}`, { method: 'DELETE' }),
  /** Quita la foto del producto (imagen vacía). */
  clearProductoImagen: async (id) => {
    // 1) Mismo endpoint de subir foto con { clear: true } (existe en hosting)
    try {
      return await request(`/admin/productos/${id}/imagen`, {
        method: 'POST',
        body: JSON.stringify({ clear: true }),
        timeoutMs: 30000,
      });
    } catch (err) {
      const msg = String(err?.message || '');
      // API sin soporte clear en POST: intenta /clear y DELETE
      if (!/404|Cannot POST|no encontrad|Falta la imagen|data_url|base64/i.test(msg)) {
        throw err;
      }
    }
    try {
      return await request(`/admin/productos/${id}/imagen/clear`, {
        method: 'POST',
        body: JSON.stringify({}),
        timeoutMs: 30000,
      });
    } catch (err2) {
      const msg2 = String(err2?.message || '');
      if (/404|Cannot POST|no encontrad/i.test(msg2)) {
        return request(`/admin/productos/${id}/imagen`, {
          method: 'DELETE',
          timeoutMs: 30000,
        });
      }
      throw err2;
    }
  },
  sincronizarCategorias: () =>
    request('/admin/categorias/sincronizar', { method: 'POST', timeoutMs: 120000 }),
  getAdminCategorias: () => request('/admin/categorias'),
  createCategoria: (body) =>
    request('/admin/categorias', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    }),
  updateCategoria: (id, body) =>
    request(`/admin/categorias/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body || {}),
    }),
  moverCategoria: (id, direccion) =>
    request(`/admin/categorias/${id}/mover`, {
      method: 'POST',
      body: JSON.stringify({ direccion }),
    }),
  deleteCategoria: (id) => request(`/admin/categorias/${id}`, { method: 'DELETE' }),
  getVendedores: () => request('/vendedores'),
  getAdminSucursales: ({
    q = '',
    ciudad = '',
    zona = '',
    vendedor_id,
    tipo = '',
    limit = 50,
    offset = 0,
    facets,
  } = {}) => {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    if (q) params.set('q', q);
    if (ciudad) params.set('ciudad', ciudad);
    if (zona) params.set('zona', zona);
    if (vendedor_id != null && vendedor_id !== '') params.set('vendedor_id', String(vendedor_id));
    if (tipo) params.set('tipo', String(tipo));
    if (facets) params.set('facets', '1');
    return request(`/sucursales?${params}`, { timeoutMs: 40000 });
  },
  crearSucursal: (body) =>
    request('/sucursales', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getSedes: (clienteId) => request(`/sucursales/${clienteId}/sedes`),
  crearSede: (clienteId, body) =>
    request(`/sucursales/${clienteId}/sedes`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  actualizarSede: (clienteId, sedeId, body) =>
    request(`/sucursales/${clienteId}/sedes/${sedeId}`, {
      method: 'PATCH',
      body: JSON.stringify(body || {}),
    }),
  eliminarSede: (clienteId, sedeId) =>
    request(`/sucursales/${clienteId}/sedes/${sedeId}`, { method: 'DELETE' }),
  crearVendedor: (body) =>
    request('/vendedores', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  actualizarVendedor: (id, body) =>
    request(`/vendedores/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  eliminarVendedor: (id) => request(`/vendedores/${id}`, { method: 'DELETE' }),
  getUsuarios: () => request('/admin/usuarios'),
  crearUsuario: (body) =>
    request('/admin/usuarios', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  actualizarUsuario: (id, body) =>
    request(`/admin/usuarios/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  eliminarUsuario: (id) => request(`/admin/usuarios/${id}`, { method: 'DELETE' }),
};
